import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import './vettest.css';
import ReadAloudTest from './readaloudtests';
import RepeatSentenceTest from './repeatsentencestest';
import ShortAnswerTest from './shortanswertests';
import SentenceBuildTest from './sentencesbuildtest';
import VocabularyTest from './vocabularytests';
import WritingTest from './writingtests';
import ReadAloudIcon from '../Icons/readaloud';
import RepeatIcon from '../Icons/repeat';
import ChatIcon from '../Icons/chat';
import SentenceIcon from '../Icons/sentence';
import VocabularyIcon from '../Icons/vocabulary';
import WritingIcon from '../Icons/writing';
import Header from '../adminHeader/adminHeader';
import Footer from '../adminFooter/Footer';
import getallvetquestions from '../../apis/vettest/getallvetquestions';
import getallvetsections from '../../apis/vettest/getallvetsections';
import { useInterview } from '../../hooks/useinterviewhook';
import { useCamera } from '../../hooks/usecamerahook';
import { useMediaPipeViolations } from '../../hooks/usemediapipeviolations';
import { Violations } from '../violations/violations';
import vetteststatusupdate from '../../apis/vettest/vetteststatusupdate';
import updatevetcandidatetestbyid from '../../apis/vettest/vetmanagecandidatetestbyid';
import assessmentprofile from '../../apis/vettest/assessmentprofile';
import ConfirmExitDialog from '../enditerview/endinterview';

const SECTION_META_BY_CODE = {
  VS01: { icon: <ReadAloudIcon />, duration: '~90s', seconds: 30, key: 'readAloud' },
  VS02: { icon: <RepeatIcon />, duration: '~60s', seconds: 60, key: 'repeatSentence' },
  VS03: { icon: <ChatIcon />, duration: '~75s', seconds: 60, key: 'shortAnswer' },
  VS04: { icon: <SentenceIcon />, duration: '~120s', seconds: 60, key: 'sentenceBuild' },
  VS05: { icon: <VocabularyIcon />, duration: '~90s', seconds: 90, key: 'vocabulary' },
  VS06: { icon: <WritingIcon />, duration: '15 min', seconds: 900, key: 'writing' }
};

const VetSteps = ({ candidateTestData: candidateTestDataProp }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [questionsData, setQuestionsData] = useState({});
  const [testBlocks, setTestBlocks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSectionLoading, setIsSectionLoading] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isTestCompleted, setIsTestCompleted] = useState(false);
  const [exitInProgress, setExitInProgress] = useState(false);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [currentQuestionId, setCurrentQuestionId] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const searchParams = new URLSearchParams(location.search);
  const candidateTestData = candidateTestDataProp || location.state?.candidateTestData;
  const candidateTestId = candidateTestData?.id || searchParams.get('ct');
  const normalizedPhoneData = {
    phoneNumber: (location.state?.phoneNumber || '').toString().trim(),
    countryCode: location.state?.userLocation?.code || location.state?.countryCode || '',
    countryCallingCode: (location.state?.countryCallingCode || location.state?.userLocation?.dialCode || '').toString().trim()
  };

  const { videoRef, previewStreamRef, cameraReady, cameraError } = useCamera();


  const { startInterview, stopInterview, isRunning, timer: recordingTimer } = useInterview(previewStreamRef);
  const mediapipeViolations = useMediaPipeViolations(videoRef, isRunning);


  useEffect(() => {
    if (!isLoading && !isExiting && !isTestCompleted && videoRef.current && previewStreamRef.current) {
      if (!videoRef.current.srcObject) {
        videoRef.current.srcObject = previewStreamRef.current;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play().catch(console.error);
        };
      }
    }
  }, [isLoading, isExiting, isTestCompleted]);


  const getVideoDuration = () => recordingTimer || 0;

  const getTotalExamVideoDuration = () => recordingTimer || 0;

  const updateTestDuration = async (duration) => {
    if (!candidateTestId) return null;
    const totalDuration = typeof duration === 'number' ? duration : getTotalExamVideoDuration();
    try {
      const payload = { id: candidateTestId, totalDuration: totalDuration };
      const response = await updatevetcandidatetestbyid(payload);
      return totalDuration;
    } catch (error) {
      console.error('updateTestDuration - Error:', error);
      return null;
    }
  };

  const sendExitEvent = async () => {
    if (exitInProgress || !candidateTestId) {
      return { success: false, message: exitInProgress ? 'Exit already in progress' : 'No candidateTestId' };
    }
    try {
      setExitInProgress(true);
      setIsExiting(true);
      const payload = { candidateTestId: candidateTestId, statuscode: 'interrupted' };
      const response = await vetteststatusupdate(payload);
      if (response?.status === 200) {
        return { success: true, message: 'Exit successful' };
      } else {
        return { success: false, message: response?.body?.message || 'Unknown error' };
      }
    } catch (error) {
      console.error('sendExitEvent - Error:', error);
      return { success: false, message: error.message };
    } finally {
      setExitInProgress(false);
    }
  };


  const performFullExit = async () => {
    if (exitInProgress) return;
    try {
      setExitInProgress(true);
      setIsExiting(true);
      const actualDuration = await stopInterview({
        shouldAnalyze: false,
        isVet: true,
        userId: candidateTestId,
        containerName: 'vetinterview'
    }) || 0;
      await updateTestDuration(actualDuration);
      assessmentprofile({
        "id": candidateTestId,
        "assessmentType": "vet",
        "userLocation": candidateTestDataProp?.userLocation,
        "phoneNumber": normalizedPhoneData.phoneNumber,
        "countryCallingCode": normalizedPhoneData.countryCallingCode,
        "countryCode": normalizedPhoneData.countryCode,
      });

      const result = await sendExitEvent();


      if (result.success && candidateTestId) {
        navigate(`/end-interview`, { state: { isVet: true, userId: candidateTestId }, replace: true });
      } else {
        navigate(`/end-interview`, { state: { isVet: true, userId: candidateTestId, exitError: result.message }, replace: true });
      }
    } catch (error) {
      console.error('performFullExit - Error:', error);
      navigate(`/end-interview`, { state: { isVet: true, userId: candidateTestId, exitError: error.message }, replace: true });
    } finally {
      setExitInProgress(false);
      setIsExiting(false);
    }
  };

  const completeTest = async () => {
    if (exitInProgress) return;
    try {
      setExitInProgress(true);
      setIsTestCompleted(true);
      setIsExiting(true);

      await vetteststatusupdate({
        candidateTestId: candidateTestId,
        statuscode: 'completed',
      });

      const actualDuration = getTotalExamVideoDuration();
      updateTestDuration(actualDuration);
      stopInterview({
        shouldAnalyze: false,
        isVet: true,
        userId: candidateTestId,
        containerName: 'vetinterview'
      })

      navigate(`/end-interview`, { state: { isVet: true, userId: candidateTestId }, replace: true });
    } catch (error) {
      console.error('completeTest - Error:', error);
      navigate(`/end-interview`, { state: { isVet: true, userId: candidateTestId, exitError: error.message }, replace: true });
    } finally {
      setExitInProgress(false);
      setIsExiting(false);
    }
  };

  const handleConfirmExit = async () => {
    setIsDialogOpen(false);
    const response = await sendExitEvent();
    if (response) {
      performFullExit()
    }
  };

  // --- Data Fetching ---
  const fetchVetSections = async () => {
    try {
      setIsLoading(true);
      setIsSectionLoading(false);
      setQuestionsData({});
      setCurrentStep(0);
      setCurrentQuestion(1);

      const sectionsResponse = await getallvetsections({ testId: candidateTestData?.testId });
      const sectionList = sectionsResponse?.data?.sections || sectionsResponse?.data || [];

      const dynamicBlocks = sectionList
        .filter((section) => {
          const isActive = section?.isActive !== false || section?.vetTestSectionWeightSectionId?.isActive !== false;
          return isActive;
        })
        .map((section) => {
          const weightData = section;
          const sectionData = section.vetTestSectionWeightSectionId || section;
          const sectionCode = sectionData?.code;
          const sectionName = sectionData?.name || 'Section';
          const meta = SECTION_META_BY_CODE[sectionCode] || {};
          return {
            id: weightData?.id,
            code: sectionCode,
            title: sectionName,
            weight: weightData?.weight || 0,
            displayOrder: weightData?.displayOrder || 0,
            icon: meta.icon || <ChatIcon />,
            duration: meta.duration || '~60s',
            seconds: meta.seconds || 60,
            key: meta.key || sectionCode?.toLowerCase().replace(/\s+/g, '') || 'unknown'
          };
        });

      setTestBlocks(dynamicBlocks);
      if (!dynamicBlocks.length) { setIsLoading(false); return; }
      if (dynamicBlocks[0]) setCurrentSectionId(dynamicBlocks[0].id);
      await fetchQuestionsForSection(dynamicBlocks[0], true);
    } catch (error) {
      console.error("fetchVetSections - Error:", error);
      setIsLoading(false);
    }
  };

  const fetchQuestionsForSection = async (sectionBlock, isInitialLoad = false) => {
    if (!sectionBlock?.id) {
      if (isInitialLoad) setIsLoading(false);
      return;
    }
    try {
      setCurrentSectionId(sectionBlock.id);
      const payload = { email: candidateTestData?.email, testId: candidateTestData?.testId, sectionId: sectionBlock.id };
      const response = await getallvetquestions(payload);
      let sectionQuestions = [];
      if (response?.data?.sections?.length) {
        const matchedSection = response.data.sections.find(
          (section) => section?.sectionId === sectionBlock.id || section?.sectionCode === sectionBlock.code
        ) || response.data.sections[0];
        sectionQuestions = matchedSection?.questions || [];
      } else if (response?.data?.questions?.length) {
        sectionQuestions = response.data.questions;
      }
      setQuestionsData((prev) => ({ ...(prev || {}), [sectionBlock.key]: sectionQuestions }));
    } catch (error) {
      console.error('fetchQuestionsForSection - Error:', error);
      setQuestionsData((prev) => ({ ...(prev || {}), [sectionBlock.key]: [] }));
    } finally {
      if (isInitialLoad) setIsLoading(false);
      setIsSectionLoading(false);
    }
  };

  // --- Effects ---
  useEffect(() => {
    const navigationEntries = performance.getEntriesByType('navigation');
    const isReload = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
    if (isReload && candidateTestId) performFullExit();
  }, []);

  useEffect(() => {
    const navigationEntries = performance.getEntriesByType('navigation');
    const isReload = navigationEntries.length > 0 && navigationEntries[0].type === 'reload';
    if (!isReload && !isTestCompleted && !isExiting) fetchVetSections();
  }, []);

  useEffect(() => {
    if (!testBlocks.length || isLoading || isExiting) return;
    const currentSectionBlock = testBlocks[currentStep];
    if (!currentSectionBlock) return;
    if (questionsData[currentSectionBlock.key]) return;
    setIsSectionLoading(true);
    fetchQuestionsForSection(currentSectionBlock, false);
  }, [currentStep, testBlocks, isLoading, isExiting]);

  const getTotalQuestionsForCurrentStep = () => {
    if (!questionsData || !testBlocks.length) return 0;
    const currentBlockKey = testBlocks[currentStep].key;
    return questionsData[currentBlockKey]?.length || 0;
  };

  const startVet = async () => {
    await startInterview({
      userId: candidateTestId,
      containerName: 'vetinterview',
      isVet: true,
      shouldMixAudio: false
    });
  };

  useEffect(() => {
    if (!testBlocks.length || isSectionLoading || isExiting) return;
    const currentBlockKey = testBlocks[currentStep]?.key;
    const currentSectionQuestions = questionsData[currentBlockKey];
    if (!currentSectionQuestions || !currentSectionQuestions.length) return;
    const currentQuestionData = currentSectionQuestions[currentQuestion - 1];
    const questionTimeLimitSeconds = currentQuestionData?.timeLimitSeconds || testBlocks[currentStep]?.seconds || 60;
    if (currentQuestionData?.id) setCurrentQuestionId(currentQuestionData.id);
    setTimeRemaining(questionTimeLimitSeconds);
    setQuestionStartTime(Date.now());
    const interval = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => { clearInterval(interval); };
  }, [currentStep, currentQuestion, testBlocks, questionsData, isSectionLoading, isExiting]);

  // --- Start Interview when camera is ready ---
  useEffect(() => {
    if (
      cameraReady &&
      !isLoading &&
      testBlocks.length &&
      !isExiting &&
      !isTestCompleted &&
      previewStreamRef.current &&
      !isRunning
    ) {
      startVet();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraReady, isLoading, testBlocks.length, isExiting, isTestCompleted, candidateTestId, isRunning]);

  // --- Browser event listeners ---
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.returnValue = 'Are you sure you want to leave? Your test progress will be lost.';
      return e.returnValue;
    };
    const handlePopState = (e) => {
      if (isExiting || exitInProgress) return;
      const confirmExit = window.confirm('Are you sure you want to go back? Your test progress will be lost.');
      if (confirmExit) {
        performFullExit();
      } else {
        window.history.pushState(null, '', window.location.pathname + window.location.search);
      }
    };
    const handleKeyDown = (e) => {
      if (isExiting || exitInProgress) return;
      const isReloadShortcut = (e.key === 'r' && (e.ctrlKey || e.metaKey)) || e.key === 'F5';
      if (isReloadShortcut) {
        e.preventDefault();
        e.stopPropagation();
        const confirmExit = window.confirm('Are you sure you want to reload? Your test progress will be lost.');
        if (confirmExit) performFullExit();
      }
    };
    window.history.pushState(null, '', window.location.pathname + window.location.search);
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [candidateTestId, navigate, isExiting, exitInProgress]);

  // --- UI Handlers ---
  const handleNextQuestion = async () => {
    if (!testBlocks.length || isExiting) return;
    const totalQuestionsPerStep = getTotalQuestionsForCurrentStep();
    if (currentQuestion < totalQuestionsPerStep) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      const currentBlockKey = testBlocks[currentStep].key;
      if (questionsData[currentBlockKey] && questionsData[currentBlockKey][nextQuestion - 1]) {
        setCurrentQuestionId(questionsData[currentBlockKey][nextQuestion - 1].id);
      }
    } else if (currentStep < testBlocks.length - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      setCurrentQuestion(1);
      if (testBlocks[nextStep]) setCurrentSectionId(testBlocks[nextStep].id);
      const newBlockKey = testBlocks[nextStep]?.key;
      if (questionsData[newBlockKey] && questionsData[newBlockKey][0]) {
        setCurrentQuestionId(questionsData[newBlockKey][0].id);
      }
    } else {
      await completeTest();
    }
  };

  const isLastQuestionOfCurrentSection = testBlocks.length > 0 && currentQuestion === getTotalQuestionsForCurrentStep();
  const isLastQuestion = testBlocks.length > 0 && currentStep === testBlocks.length - 1 && isLastQuestionOfCurrentSection;

  const renderTestComponent = () => {
    if (!testBlocks.length || isSectionLoading || isExiting) return <div>Loading questions...</div>;
    const currentSection = testBlocks[currentStep];
    const componentKey = `${currentStep}-${currentQuestion}`;
    const currentBlockKey = currentSection.key;
    let questionData = null;
    if (questionsData[currentBlockKey] && questionsData[currentBlockKey][currentQuestion - 1]) {
      questionData = questionsData[currentBlockKey][currentQuestion - 1];
    }
    const commonProps = {
      questionNumber: currentQuestion,
      onNext: handleNextQuestion,
      isLastQuestion: isLastQuestion,
      isLastQuestionOfSection: isLastQuestionOfCurrentSection,
      currentSection: currentSection,
      questionData: questionData,
      candidateTestId: candidateTestId,
      timeRemaining: timeRemaining,
      getVideoDuration: getVideoDuration,
      getTotalExamVideoDuration: getTotalExamVideoDuration,
      sectionQuestions: questionsData[currentBlockKey] || []
    };
    switch (currentSection.title) {
      case 'Read Aloud': return <ReadAloudTest key={componentKey} {...commonProps} />;
      case 'Repeat Sentence': return <RepeatSentenceTest key={componentKey} {...commonProps} />;
      case 'Short Answer': return <ShortAnswerTest key={componentKey} {...commonProps} />;
      case 'Sentence Build': return <SentenceBuildTest key={componentKey} {...commonProps} />;
      case 'Vocabulary': return <VocabularyTest key={componentKey} {...commonProps} />;
      case 'Writing': return <WritingTest key={componentKey} {...commonProps} />;
      default: return <div>Test type not found</div>;
    }
  };

  const formatTime = (seconds) => `${seconds}s`;

  if (isLoading || !questionsData) {
    return (
      <>
        <Header />
        <div className="vet-steps-container"><div className="steps-layout"><div className="loading-screen">Loading test questions...</div></div></div>
        <Footer />
      </>
    );
  }

  if (!testBlocks.length) {
    return (
      <>
        <Header />
        <div className="vet-steps-container"><div className="steps-layout"><div className="loading-screen">No active VET sections found.</div></div></div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="vet-steps-container">
        <div className="steps-layout">
          <div className="steps-camera-section">
            <div className="camera-container-steps">
              <div className="camera-preview-steps">
                {cameraError ? (
                  <>
                    <div className="camera-icon">📹</div>
                    <p className="camera-error-text">{cameraError}</p>
                    <button className="btn-retry-camera" onClick={() => window.location.reload()}>Retry Camera Access</button>
                  </>
                ) : (
                  <video ref={videoRef} autoPlay playsInline muted className="camera-video-steps" />
                )}
              </div>
              <div className="camera-info-steps"><p>{cameraReady ? "Camera Active" : "Initializing camera..."}</p></div>
              <div className="test-warnings">
                <h4 className="warnings-title">Test Guidelines</h4>
                <ul className="warnings-list">
                  <li>Keep your face visible at all times</li>
                  <li>Do not switch tabs or windows</li>
                  <li>No external help or resources</li>
                  <li>Do not navigate away from test</li>
                  <li>Ensure stable internet connection</li>
                </ul>
              </div>
              <div className="vet-violations-container">
                <Violations userId={candidateTestId} violations={mediapipeViolations} timer={recordingTimer} isInterviewCompleted={isTestCompleted} isVet={true} sectionId={currentSectionId} questionId={currentQuestionId} />
              </div>
              <div className="end-interview-container">
                <button
                  className="end-interview-btn"
                  onClick={() => setIsDialogOpen(true)}
                >
                  End Interview
                </button>
              </div>
            </div>
          </div>

          <ConfirmExitDialog
            isOpen={isDialogOpen}
            onConfirm={handleConfirmExit}
            onCancel={() => setIsDialogOpen(false)}
          />


          <div className="steps-content-section">
            <div className="stepper-header">
              <div className="stepper-progress-bar">
                {testBlocks.map((block, index) => (
                  <div key={index} className="stepper-item">
                    <div className={`stepper-circle ${index === currentStep ? 'active' : index < currentStep ? 'completed' : ''}`}>
                      {index < currentStep ? '✓' : index + 1}
                    </div>
                    <div className="stepper-label">{block.title}</div>
                    {index < testBlocks.length - 1 && <div className={`stepper-line ${index < currentStep ? 'completed' : ''}`}></div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="step-content">
              <div className="step-header">
                <div className="step-icon-container">
                  <div className="step-icon-large">{testBlocks[currentStep].icon}</div>
                  <h2 className="step-title">{testBlocks[currentStep].title}</h2>
                  <p className={`step-duration ${timeRemaining <= 10 ? 'time-warning' : ''}`}>Time Remaining: {formatTime(timeRemaining)}</p>
                </div>
                <div className="question-progress">Question {currentQuestion} of {getTotalQuestionsForCurrentStep()}</div>
              </div>
              <div className="step-body">{renderTestComponent()}</div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default VetSteps;